import styled from '@emotion/styled';
import Button from './Button';
import WarningIcon from '~/icons/WarningIcon';

interface BillingBannerProps {
  variant: 'error' | 'warning';
  title: string;
  description: string;
  buttonText?: string;
  onButtonClick?: () => void;
}

const BannerContainer = styled.div<{ variant: 'error' | 'warning' }>`
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  padding: 12px 16px;
  border-radius: 4px;
  margin-bottom: 16px;
  gap: 8px;

  @media only screen and (min-width: 900px) {
    flex-direction: row;
    align-items: center;
  }

  ${({ variant }) =>
    variant === 'error'
      ? `
    background-color: #fef2f2;
    border: 1px solid #fecaca;
  `
      : `
    background-color: #fffbeb;
    border: 1px solid #fed7aa;
  `}
`;

const BannerContent = styled.div`
  display: flex;
  gap: 12px;

  svg {
    min-width: 24px;
  }

  @media only screen and (min-width: 900px) {
    align-items: center;
  }
`;

const BannerText = styled.div<{ variant: 'error' | 'warning' }>`
  ${({ variant }) =>
    variant === 'error'
      ? `
    color: #7f1d1d;
  `
      : `
    color: #92400e;
  `}
`;

const BannerTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 6px;
`;

const BannerDescription = styled.div<{ variant: 'error' | 'warning' }>`
  font-size: 13px;
  line-height: normal;
  font-weight: 500;

  ${({ variant }) =>
    variant === 'error'
      ? `
    color: #991b1b;
  `
      : `
    color: #a16207;
  `}
`;

const BannerActions = styled.div`
  display: flex;
  gap: 8px;
  margin-left: 32px;

  @media only screen and (min-width: 900px) {
    margin-left: 0;
  }
`;

const BillingBanner = ({
  variant,
  title,
  description,
  buttonText,
  onButtonClick,
}: BillingBannerProps) => {
  return (
    <BannerContainer variant={variant}>
      <BannerContent>
        <WarningIcon fill={variant === 'error' ? '#7f1d1d' : '#a16207'} />
        <BannerText variant={variant}>
          <BannerTitle>{title}</BannerTitle>
          <BannerDescription variant={variant}>{description}</BannerDescription>
        </BannerText>
      </BannerContent>
      {buttonText && onButtonClick && (
        <BannerActions>
          <Button variant="secondary" size="sm" onClick={onButtonClick}>
            {buttonText}
          </Button>
        </BannerActions>
      )}
    </BannerContainer>
  );
};

export default BillingBanner;
